import React, { useEffect } from 'react';
import s from './SetupMobile.scss';
import { Box, Cell, Text, Layout, Heading, Button } from '@wix/design-system';
import { CircleSmallFilled, Check } from '@wix/wix-ui-icons-common';
import { useTranslation } from '@wix/wix-i18n-config';
import { useMobileShare } from './useMobileShare';
import BannerImage from '../../components/SetupPageMobile/assets/banner-graphics.svg';
import { useMsIdAtomValue, useDsUserInsights } from '../../context/dsUserInsights';
import { useAppNavigation } from '../../hooks/useAppNavigation';
import { useBiLogger } from '../../hooks/useBiLogger/useBiLogger';
import { useEndFlowRedirect, EndFlowType } from '../../hooks/useEndFlowRedirect';
import { CATEGORIES_URL } from '../../consts';

interface SetupActionProps {
  actionButtonLabel?: string;
  actionButtonOnClick?: () => void;
  description: string;
  isCompleted?: boolean;
}

const SetupAction: React.FC<SetupActionProps> = ({
  actionButtonLabel,
  actionButtonOnClick,
  description,
  isCompleted = false,
}) => (
  <Box border="1px solid #D6E6FE" padding="SP2" direction="horizontal" gap="SP2" borderRadius="6px">
    <Box width="SP4" verticalAlign="middle" color={isCompleted ? 'G10' : 'B10'}>
      {isCompleted ? <Check /> : <CircleSmallFilled />}
    </Box>
    <Box direction="vertical" gap="SP2" align="left">
      <Text
        className={isCompleted ? s.textStrikethrough : ''}
        weight="normal"
        skin={isCompleted ? 'disabled' : 'standard'}
      >
        {description}
      </Text>
      {actionButtonLabel && (
        <Button size="small" priority="secondary" onClick={actionButtonOnClick}>
          {actionButtonLabel}
        </Button>
      )}
    </Box>
  </Box>
);

export const SetupMobile: React.FC = () => {
  const { t } = useTranslation();
  const { isNativeShareSupported, share } = useMobileShare();
  const msId = useMsIdAtomValue();
  const { navigate } = useAppNavigation();
  const plainUserInsights = useDsUserInsights();
  const biLogger = useBiLogger();
  const url = useEndFlowRedirect(EndFlowType.EDITOR);

  const setupActions: SetupActionProps[] = [
    {
      description: 'Choose products for your store',
      isCompleted: true,
    },
    {
      description: 'Getting to Know you',
      isCompleted: true,
    },
    {
      description: t('MOBILE_SETUP_PAGE_ACTIONS_DESKTOP_DASHBOARD_DESCRIPTION'),
      actionButtonLabel: t('MOBILE_SETUP_PAGE_ACTIONS_DESKTOP_DASHBOARD_CTA_LABEL'),
      actionButtonOnClick: () => {
        const { summary: storeNameSummary } = plainUserInsights?.storeName;
        const subject = encodeURIComponent(
          `Continue design your${storeNameSummary ? ` ${storeNameSummary}` : ''} online store!`,
        );
        const body = encodeURIComponent(
          `Click on the link to finish your store design on desktop and start selling today!\nUse the same Wix account you're logged in with on your phone:\n\n${url}`,
        );
        const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
        biLogger.generateSite(`mailto:?subject=${subject}&body=${body}`);
        window.location.href = mailtoLink;

        if (isNativeShareSupported) {
          share({ title: 'Wix Business Starter', url });
        } else {
          window.open(url, '_blank');
        }
      },
      isCompleted: false,
    },
  ];

  useEffect(() => {
    if (!msId) {
      navigate(CATEGORIES_URL);
    }
  }, [msId]);

  // useEffect(() => {
  //   biLogger.screenView('setup');
  //   setIsBusinessDetailsModalOpen(false);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  return (
    <>
      <Layout className={s.setupPageLayout}>
        <Cell span={12}>
          <Box direction="horizontal" className={s.setupPageBanner} gap="SP2">
            <BannerImage height={96} />
            <Box direction="vertical" verticalAlign="middle">
              <Text size="medium" weight="bold">
                {t('MOBILE_SETUP_PAGE_BANNER_HEADING')}
              </Text>

              <Text size="medium">{t('MOBILE_SETUP_PAGE_BANNER_CONTENT')}</Text>
            </Box>
          </Box>
        </Cell>

        <Cell span={12}>
          <Box padding="0 SP3">
            <Heading size="medium" weight="bold">
              {t('MOBILE_SETUP_PAGE_STEPS_HEADER')}
            </Heading>
          </Box>
        </Cell>

        <Cell span={12}>
          <Box padding="0 SP3" direction="vertical" gap="SP2">
            {setupActions.map(({ description, actionButtonLabel, actionButtonOnClick, isCompleted }, index) => (
              <SetupAction
                key={`setup-action-${index}`}
                description={description}
                actionButtonLabel={actionButtonLabel}
                actionButtonOnClick={actionButtonOnClick}
                isCompleted={isCompleted}
              />
            ))}
          </Box>
        </Cell>
      </Layout>
    </>
  );
};
